<template>
    <div ref="host" class="e-toast-host" :class="{ wrapper: asWrapper, 'wrapper-full': asWrapper && fullWidth }">
        <slot v-if="asWrapper" v-bind="slotData"/>

        <transition name="e-toast-fade" appear>
            <div ref="toast" v-if="currentVisible" class="e-toast"
                 :class="[ position, variant, size,
                     {
                        'fixed': fixed && !asWrapper, 'wrapper': asWrapper, 'translucent': translucent,
                        'body-portal': useBodyPortal, 'from-bottom': position.indexOf(`bottom`) === 0
                     }
                 ]"
                 :style="style" role="status" :aria-live="ariaLive"
            >
                <div class="surface" :class="{ interactive: !hideClose }">
                    <div v-if="!hideIcon" class="icon-wrap">
                        <slot name="icon">
                            <i :class="iconClass"/>
                        </slot>
                    </div>

                    <div class="content">
                        <div v-if="hasTitle" class="title">
                            <slot name="title">{{ title }}</slot>
                        </div>
                        <div v-if="hasBody" class="message">
                            <slot v-if="asWrapper" name="toast">{{ bodyText }}</slot>
                            <slot v-else v-bind="slotData">{{ bodyText }}</slot>
                        </div>
                    </div>

                    <button v-if="!hideClose" @click.stop="close" type="button" class="close" :aria-label="closeAriaLabel" >
                        <slot name="close">
                            <i :class="closeClass"/>
                        </slot>
                    </button>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
const POSITION_VALUES = [
    `top-left`,
    `top-center`,
    `top-right`,
    `center`,
    `bottom-left`,
    `bottom-center`,
    `bottom-right`,
];

const VARIANT_VALUES = [`success`, `info`, `warning`, `danger`];

const VARIANT_ICONS = {
    success: `fa-circle-check`,
    info: `fa-circle-info`,
    warning: `fa-triangle-exclamation`,
    danger: `fa-circle-exclamation`
};

const VIEWPORT_EDGE_GAP_PX = 12;

export default {
    name: `e-toast`,
    props: {
        visible: {type: Boolean, default: true},
        title: {type: String, default: ``},
        message: {type: String, default: ``},
        description: {type: String, default: ``},
        position: {
            type: String, default: `top-center`,
            validator(value) {
                return POSITION_VALUES.includes(value);
            }
        },
        variant: {
            type: String, default: `info`,
            validator(value) {
                return VARIANT_VALUES.includes(value);
            }
        },
        fixed: {type: Boolean, default: false},
        asWrapper: {type: Boolean, default: false},
        fullWidth: {type: Boolean, default: false},
        hideIcon: {type: Boolean, default: false},
        icon: {type: String, default: ``},
        iconPrefix: {type: String, default: `fas`},
        hideClose: {type: Boolean, default: true},
        closeIcon: {type: String, default: `fa-xmark`},
        closeIconPrefix: {type: String, default: `fas`},
        closeAriaLabel: {type: String, default: `Close notification`},
        ariaLive: {type: String, default: `polite`},
        offset: {type: String, default: ``},
        autoCloseMs: {type: Number, default: 0},
        wrapperAutoCloseMs: {type: Number, default: 2600},
        renderInBodyForWrapper: {type: Boolean, default: true},
        translucent: {type: Boolean},
        zIndex: {type: Number, default: 1022},
        offsetX: {type: String, default: `0px`},
        offsetY: {type: String, default: `0px`},
        size: {type: String, default: `md`},
        maxWidth: {type: String, default: ``}
    },
    data() {
        const hasVisibleBinding = Object.prototype.hasOwnProperty.call(this.$options.propsData || {}, `visible`);

        return {
            autoCloseTimer: null,
            portalRaf: null,
            portal: null,
            portalBounds: null,
            portalSnapshot: ``,
            hasVisibleBinding,
            currentVisible: this.asWrapper && !hasVisibleBinding ? false : this.visible
        };
    },
    computed: {
        useBodyPortal() {
            return this.asWrapper && this.renderInBodyForWrapper;
        },
        autoCloseTime() {
            if (this.autoCloseMs > 0) {
                return this.autoCloseMs;
            }
            if (this.asWrapper && !this.hasVisibleBinding && this.wrapperAutoCloseMs > 0) {
                return this.wrapperAutoCloseMs;
            }
            return 0;
        },
        resolvedOffsetX() {
            return this.offsetX;
        },
        resolvedOffsetY() {
            if (this.offset) {
                return this.offset;
            }

            return this.offsetY;
        },
        resolvedMaxWidth() {
            if (this.maxWidth) {
                return this.maxWidth;
            }

            return `calc(100vw - 24px)`;
        },
        slotData() {
            return {
                open: this.open,
                close: this.close,
                toggle: this.toggle,
                visible: this.currentVisible
            };
        },
        style() {
            const style = {
                zIndex: this.zIndex,
                '--e-toast-offset-x': this.offsetX,
                '--e-toast-offset-y': this.offset || this.offsetY,
                '--e-toast-max-width': this.resolvedMaxWidth
            };

            if (!this.useBodyPortal || !this.portalBounds) {
                return style;
            }

            style.position = `fixed`;
            style.top = `${this.portalBounds.top}px`;
            style.left = `${this.portalBounds.left}px`;
            style.transform = `none`;
            style.width = `${this.portalBounds.width}px`;

            return style;
        },
        bodyText() {
            if (this.description) {
                return this.description;
            }

            return this.message;
        },
        hasTitle() {
            return Boolean(this.title || this.$slots.title);
        },
        hasBody() {
            if (this.asWrapper) {
                return Boolean(this.bodyText || this.$slots.toast);
            }
            return Boolean(this.bodyText || this.$slots.default);
        },
        iconClass() {
            if (this.icon) {
                if (this.icon.includes(` `)) {
                    return this.icon;
                }
                return [this.iconPrefix, this.icon];
            }
            return [this.iconPrefix, VARIANT_ICONS[this.variant]];
        },
        closeClass() {
            if (this.closeIcon.includes(` `)) {
                return this.closeIcon;
            }
            return [this.closeIconPrefix, this.closeIcon];
        }
    },
    watch: {
        visible(isVisible) {
            if (this.asWrapper && !this.hasVisibleBinding) {
                return;
            }

            this.currentVisible = isVisible;
        },
        currentVisible() {
            this.syncToastState();
        },
        autoCloseTime() {
            this.refreshAutoClose();
        }
    },
    mounted() {
        this.syncToastState();
    },
    updated() {
        if (!this.useBodyPortal) {
            this.moveToastToHost();
            this.portalBounds = null;
            this.portalSnapshot = ``;
            this.stopPortalSync();
            this.removeBodyPortalLayer();
            return;
        }

        if (!this.currentVisible) {
            return;
        }

        this.syncBodyPortal();
        this.startPortalSync();
    },
    beforeDestroy() {
        this.clearAutoCloseTimer();
        this.stopPortalSync();
        this.removeBodyPortalLayer();
    },
    methods: {
        syncToastState() {
            this.refreshAutoClose();

            if (!this.useBodyPortal) {
                this.moveToastToHost();
                this.portalBounds = null;
                this.portalSnapshot = ``;
                this.stopPortalSync();
                this.removeBodyPortalLayer();
                return;
            }

            if (!this.currentVisible) {
                this.moveToastToHost();
                this.clearPortalPresentation();
                this.portalBounds = null;
                this.portalSnapshot = ``;
                this.stopPortalSync();
                this.removeBodyPortalLayer();
                return;
            }

            this.$nextTick(() => {
                this.ensureBodyPortalLayer();
                this.updatePortal();
                this.syncBodyPortal();
                this.startPortalSync();
            });
        },
        refreshAutoClose() {
            if (!this.currentVisible || this.autoCloseTime <= 0) {
                this.clearAutoCloseTimer();
                return;
            }

            this.scheduleAutoClose();
        },
        ensureBodyPortalLayer() {
            if (this.portal) {
                return;
            }

            let layerEl = document.getElementById(`e-toast-global-layer`);

            if (!layerEl) {
                layerEl = document.createElement(`div`);
                layerEl.id = `e-toast-global-layer`;
                document.body.appendChild(layerEl);
            }

            this.portal = layerEl;
        },
        syncBodyPortal() {
            if (!this.useBodyPortal) {
                return;
            }

            const toastEl = this.$refs.toast;

            if (!toastEl || !this.portal || toastEl.parentNode === this.portal) {
                return;
            }

            this.portal.appendChild(toastEl);
        },
        moveToastToHost() {
            const hostEl = this.$refs.host;
            const toastEl = this.$refs.toast;

            if (!hostEl || !toastEl || toastEl.parentNode === hostEl) {
                return;
            }

            hostEl.appendChild(toastEl);
        },
        clearPortalPresentation() {
            const toastEl = this.$refs.toast;

            if (!toastEl) {
                return;
            }

            toastEl.style.position = ``;
            toastEl.style.top = ``;
            toastEl.style.left = ``;
            toastEl.style.transform = ``;
            toastEl.style.width = ``;
        },
        removeBodyPortalLayer() {
            if (!this.portal) {
                return;
            }

            const layerEl = this.portal;
            this.portal = null;

            if (layerEl.childElementCount === 0 && layerEl.parentNode) {
                layerEl.parentNode.removeChild(layerEl);
                return;
            }

            // If Vue removes the toast element just after this hook, clean the layer on next tick.
            setTimeout(() => {
                if (layerEl.childElementCount === 0 && layerEl.parentNode) {
                    layerEl.parentNode.removeChild(layerEl);
                }
            }, 0);
        },
        stopPortalSync() {
            if (this.portalRaf) {
                cancelAnimationFrame(this.portalRaf);
                this.portalRaf = null;
            }
        },
        startPortalSync() {
            if (this.portalRaf) {
                return;
            }

            const sync = () => {
                if (!this.useBodyPortal || !this.currentVisible) {
                    this.portalRaf = null;
                    return;
                }

                this.updatePortal();
                this.syncBodyPortal();
                this.portalRaf = requestAnimationFrame(sync);
            };

            this.portalRaf = requestAnimationFrame(sync);
        },
        updatePortal() {
            if (!this.useBodyPortal) {
                this.portalBounds = null;
                this.portalSnapshot = ``;
                return;
            }

            const hostEl = this.$refs.host;
            const toastEl = this.$refs.toast;

            if (!hostEl || !toastEl) {
                return;
            }

            const hostRect = hostEl.getBoundingClientRect();
            const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
            const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
            const snapshot = [
                Math.round(hostRect.top),
                Math.round(hostRect.left),
                Math.round(hostRect.width),
                Math.round(hostRect.height),
                Math.round(toastEl.offsetWidth),
                Math.round(toastEl.offsetHeight),
                viewportWidth,
                viewportHeight
            ].join(`:`);

            if (this.portalSnapshot === snapshot) {
                return;
            }

            this.portalSnapshot = snapshot;

            const wasInPortal = toastEl.parentNode === this.portal;

            if (wasInPortal) {
                hostEl.appendChild(toastEl);
                this.clearPortalPresentation();
            }

            const rect = toastEl.getBoundingClientRect();

            if (wasInPortal) {
                this.portal.appendChild(toastEl);
            }

            const nextBounds = this.clampToViewport(rect);

            if (!this.portalBounds
                || this.portalBounds.top !== nextBounds.top
                || this.portalBounds.left !== nextBounds.left
                || this.portalBounds.width !== nextBounds.width
            ) {
                this.portalBounds = nextBounds;
            }
        },
        clampToViewport(rect) {
            const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
            const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
            const width = Math.min(rect.width, Math.max(0, viewportWidth - (VIEWPORT_EDGE_GAP_PX * 2)));
            const maxTop = Math.max(VIEWPORT_EDGE_GAP_PX, viewportHeight - rect.height - VIEWPORT_EDGE_GAP_PX);
            const maxLeft = Math.max(VIEWPORT_EDGE_GAP_PX, viewportWidth - width - VIEWPORT_EDGE_GAP_PX);
            let top = rect.top;
            let left = rect.left;

            if (top < VIEWPORT_EDGE_GAP_PX) {
                top = VIEWPORT_EDGE_GAP_PX;
            }
            if (top > maxTop) {
                top = maxTop;
            }
            if (left < VIEWPORT_EDGE_GAP_PX) {
                left = VIEWPORT_EDGE_GAP_PX;
            }
            if (left > maxLeft) {
                left = maxLeft;
            }

            return { top, left, width };
        },
        setVisible(isVisible) {
            this.currentVisible = isVisible;
            this.$emit(`input`, isVisible);
            this.$emit(`update:visible`, isVisible);
        },
        open() {
            this.setVisible(true);
            this.$emit(`open`);
        },
        scheduleAutoClose() {
            this.clearAutoCloseTimer();
            this.autoCloseTimer = setTimeout(() => {
                this.close();
            }, this.autoCloseTime);
        },
        clearAutoCloseTimer() {
            if (!this.autoCloseTimer) {
                return;
            }
            clearTimeout(this.autoCloseTimer);
            this.autoCloseTimer = null;
        },
        close() {
            this.setVisible(false);
            this.$emit(`close`);
            this.$emit(`dismiss`);
        },
        toggle() {
            if (this.currentVisible) {
                this.close();
                return;
            }
            this.open();
        }
    }
};
</script>

<style scoped lang="scss">
.e-toast-host {
    position: static;
    z-index: 1056;
    height: 0;
    width: 0;
}

.e-toast-host.wrapper {
    position: relative;
    display: inline-flex;
    width: 100%;
    max-width: 100%;
    height: auto;
}

.e-toast-host.wrapper-full {
    display: block;
    width: 100%;
}

.e-toast {
    position: absolute;
    top: var(--e-toast-offset-y);
    left: 50%;
    transform: translateX(-50%);
    width: max-content;
    max-width: var(--e-toast-max-width);
    pointer-events: none;

    --e-toast-icon-size: 42px;
}

.e-toast.fixed {
    position: fixed;
}

.e-toast.top-left {
    left: var(--e-toast-offset-x);
    right: auto;
    transform: none;
}

.e-toast.top-right {
    right: var(--e-toast-offset-x);
    left: auto;
    transform: none;
}

.e-toast.center {
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
}

.e-toast.bottom-left,
.e-toast.bottom-center,
.e-toast.bottom-right {
    top: auto;
    bottom: var(--e-toast-offset-y);
}

.e-toast.bottom-left {
    left: var(--e-toast-offset-x);
    right: auto;
    transform: none;
}

.e-toast.bottom-center {
    max-width: var(--e-toast-max-width);
}

.e-toast.bottom-right {
    right: var(--e-toast-offset-x);
    left: auto;
    transform: none;
}

.e-toast.wrapper {
    width: max-content;
    min-width: 0;
    max-width: var(--e-toast-max-width);
}

.e-toast.body-portal {
    min-width: 0;
    max-width: var(--e-toast-max-width);
}

.e-toast-host.wrapper-full .e-toast.wrapper {
    width: 100%;
}

.e-toast-host.wrapper-full .e-toast.wrapper .surface {
    width: 100%;
}

.e-toast-host.wrapper .e-toast.top-center {
    top: 0;
    left: 50%;
    transform: translate(calc(-50% - var(--e-toast-offset-x)), calc(-100% - var(--e-toast-offset-y)));
}

.e-toast-host.wrapper .e-toast.top-left {
    top: 0;
    left: 0;
    transform: translate(var(--e-toast-offset-x), calc(-100% - var(--e-toast-offset-y)));
}

.e-toast-host.wrapper .e-toast.top-right {
    top: 0;
    right: 0;
    transform: translate(var(--e-toast-offset-x), calc(-100% - var(--e-toast-offset-y)));
}

.e-toast-host.wrapper .e-toast.bottom-center {
    top: 100%;
    bottom: auto;
    left: 50%;
    transform: translate(calc(-50% - var(--e-toast-offset-x)), var(--e-toast-offset-y));
}

.e-toast-host.wrapper .e-toast.bottom-left {
    top: 100%;
    bottom: auto;
    left: 0;
    transform: translate(var(--e-toast-offset-x), var(--e-toast-offset-y));
}

.e-toast-host.wrapper .e-toast.bottom-right {
    top: 100%;
    bottom: auto;
    right: 0;
    transform: translate(var(--e-toast-offset-x), var(--e-toast-offset-y));
}

.e-toast-host.wrapper .e-toast.center {
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
}

.e-toast .surface {
    --e-toast-color: var(--color-info);
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    max-width: 100%;
    padding: 10px 24px 10px 14px;
    border-radius: 999px;
    color: black;
    background: white;
    border: 1px solid var(--e-toast-color);
    background-clip: padding-box;
    box-shadow: rgba(0, 0, 0, 0.1) 0 1px 3px 0,
    rgba(0, 0, 0, 0.1) 0 1px 2px -1px;

    pointer-events: none;
}

.e-toast .surface.interactive {
    pointer-events: auto;
}

.e-toast.success .surface {
    --e-toast-color: var(--color-success);
    --e-toast-light-color: var(--color-light-success);
}

.e-toast.info .surface {
    --e-toast-color: var(--color-info);
    --e-toast-light-color: var(--color-light-info);
}

.e-toast.warning .surface {
    --e-toast-color: var(--color-warning);
    --e-toast-light-color: var(--color-light-warning);
}

.e-toast.danger .surface {
    --e-toast-color: var(--color-danger);
    --e-toast-light-color: var(--color-light-danger);
}

.e-toast.translucent .surface {
    background: rgba(255, 255, 255, 0.8);
    backdrop-filter: blur(8px) brightness(1.1);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);
}

.e-toast.sm {
    --e-toast-icon-size: 38px;

    .surface {
        gap: 8px;
        padding: 6px 14px 6px 6px;
        min-height: initial;
    }
}

.e-toast.xs {
    --e-toast-icon-size: 32px;

    .surface {
        gap: 6px;
        padding: 4px 12px 4px 6px;
        min-height: initial;
    }
}

.e-toast .icon-wrap {
    flex: 0 0 var(--e-toast-icon-size);
    width: var(--e-toast-icon-size);
    height: var(--e-toast-icon-size);
    border-radius: 999px;
    background: var(--e-toast-color);
    color: white;
    display: grid;
    place-items: center;
    font-size: 1.08rem;
}

.e-toast .content {
    min-width: 0;
    text-align: left;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 2px;
}

.e-toast .title {
    font-size: 11pt;
    font-weight: bold;
    line-height: 1.15;
}

.e-toast .message {
    font-size: 10pt;
    font-weight: 400;
    line-height: 1.2;
    opacity: 0.95;
    text-wrap: pretty;
}

.e-toast .close {
    flex: 0 0 42px;
    margin-left: auto;
    width: 42px;
    height: 42px;
    border: 1px solid rgba(255, 255, 255, 0.38);
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.06);
    color: inherit;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: background 160ms ease, transform 160ms ease;
}

.e-toast .close:hover {
    background: rgba(255, 255, 255, 0.14);
}

.e-toast .close:active {
    transform: scale(0.94);
}

.e-toast-fade-enter-active .surface,
.e-toast-fade-leave-active .surface {
    transition: opacity 220ms ease, transform 220ms ease;
}

.e-toast-fade-enter .surface,
.e-toast-fade-leave-to .surface {
    opacity: 0;
    transform: translateY(-8px) scale(0.98);
}

.e-toast.from-bottom.e-toast-fade-enter .surface,
.e-toast.from-bottom.e-toast-fade-leave-to .surface {
    transform: translateY(8px) scale(0.98);
}
</style>
